import { useCallback, useMemo, useRef } from 'react';

import { useFireAnalyticsEvents } from '@/hooks/use-fire-analytics-events';

import { fetchProductCatalogData } from '../client/client';
import { CollectionKey, CollectionPlan, CollectionsInputs } from '../context/types';
import { useUbpContext, UbpStep } from '../context/ubp-context';
import { calculateAllowances, calculateUsage } from '../model/calculate.ts';
import { offeringAllowances as offeringAllowancesFallbackData, skus, Sku } from '../model/data';

export const useInputValues = () => useUbpContext().inputs.values;
export const useInputSetters = () => useUbpContext().inputs.setters;

export const useStep = () => useUbpContext().step;
export const useSetStep = () => useUbpContext().setStep;

export const useHasCollectionSelected = () => {
	const { collections } = useInputValues();
	return Object.values(collections).some((c) => c.selected);
};

export const useSetCollectionSelected = () => {
	const { setCollections } = useInputSetters();
	return useCallback(
		(key: CollectionKey, selected: boolean) => {
			setCollections((prev) => ({
				...prev,
				[key]: { ...prev[key], selected },
			}));
		},
		[setCollections],
	);
};

export const useSetCollectionPlan = () => {
	const { setCollections } = useInputSetters();
	return useCallback(
		(key: CollectionKey, plan: CollectionPlan) => {
			setCollections((prev) => ({
				...prev,
				[key]: { ...prev[key], plan },
			}));
		},
		[setCollections],
	);
};

export const useUsageOutput = () => {
	const inputs = useInputValues();
	return useMemo(() => calculateUsage(inputs), [inputs]);
};

export const useAiCreditsExceeded = () => {
	const usage = useUsageOutput();
	const allowances = useAllowancesOutput();
	const step = useStep();
	if (step === UbpStep.collections) {
		return false;
	}

	return usage.aiCredits > allowances.aiCredits;
};

export const useAllowancesOutput = () => {
	const inputs = useInputValues();
	const allowanceConfigs = useProductCatalogData();

	return useMemo(() => {
		if (!allowanceConfigs) {
			return {
				aiCredits: 0,
				indexedObjects: 0,
			};
		}

		const allowances = calculateAllowances({
			allowanceConfigs,
			inputs: {
				...inputs,
				offeringSkus: collectionsToSkus(inputs.collections),
			},
		});

		return {
			...allowances,
		};
	}, [inputs]);
};

const COLLECTION_SKU_MAP: Record<CollectionKey, Record<CollectionPlan, Sku>> = {
	teamwork: {
		free: skus.teamworkCollectionFree,
		standard: skus.teamworkCollectionStandard,
		premium: skus.teamworkCollectionPremium,
		enterprise: skus.teamworkCollectionEnterprise,
	},
	service: {
		free: skus.serviceCollectionFree,
		standard: skus.serviceCollectionStandard,
		premium: skus.serviceCollectionPremium,
		enterprise: skus.serviceCollectionEnterprise,
	},
	strategy: {
		free: skus.teamworkCollectionFree,
		standard: skus.teamworkCollectionStandard,
		premium: skus.teamworkCollectionPremium,
		enterprise: skus.teamworkCollectionEnterprise,
	},
	software: {
		free: skus.teamworkCollectionFree,
		standard: skus.teamworkCollectionStandard,
		premium: skus.teamworkCollectionPremium,
		enterprise: skus.teamworkCollectionEnterprise,
	},
};

const collectionsToSkus = (collections: CollectionsInputs): Sku[] => {
	return (Object.keys(collections) as CollectionKey[])
		.filter((key) => collections[key].selected)
		.map((key) => COLLECTION_SKU_MAP[key][collections[key].plan]);
};

export const useLoadProductCatalogData = () => {
	const { setProductCatalog } = useUbpContext();
	const fireAnalyticsEvent = useFireAnalyticsEvents();

	// `useFireAnalyticsEvents` returns a fresh object literal every render, so
	// including it directly in the useCallback dependency array would cause the
	// returned function to change identity every render — which breaks any
	// consumer that puts this function in a useEffect dependency array
	// (infinite render loop: effect fires → setState → re-render → new fn ref
	// → effect fires again). Stash the latest value in a ref so the callback
	// can read the current `fireAnalyticsEvent` without depending on it.
	const fireAnalyticsEventRef = useRef(fireAnalyticsEvent);
	fireAnalyticsEventRef.current = fireAnalyticsEvent;

	// Only depend on the truly stable `setProductCatalog` (from useState). This
	// gives the returned function a stable identity across renders, safe to
	// pass into a useEffect dependency array.
	return useCallback(
		async function loadProductCatalogData() {
			setProductCatalog({ loading: true, data: null, error: null });
			try {
				const data = await fetchProductCatalogData(Object.values(skus));
				setProductCatalog({ loading: false, data, error: null });
			} catch (error: unknown) {
				setProductCatalog({
					loading: false,
					data: offeringAllowancesFallbackData,
					error: null,
				});

				const errorData = error as Error;
				fireAnalyticsEventRef.current.fireOperationalEvent({
					action: 'failed',
					actionSubject: 'ubpProductCatalogDataFetch',
					attributes: {
						errorMessage: errorData?.message,
					},
				});
			}
		},
		[setProductCatalog],
	);
};

export const useIsProductCatalogDataReady = () => {
	const { productCatalog } = useUbpContext();
	return !productCatalog.loading && !!productCatalog.data;
};

export const useProductCatalogData = () => {
	return useUbpContext().productCatalog.data;
};
